import React, { useState } from 'react';
import { Activity, Award, Calendar, MessageCircle, BarChart, Users, GitBranch, Microscope, Thermometer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import AppLayout from './AppLayout';
import MessageCenter from './MessageCenter';
import './Home.css';

const cardClass = 'cell-card bg-white p-6 rounded-3xl shadow-lg cytoplasm-bg';
const buttonClass = 'w-full py-3 px-4 bg-gradient-to-r from-[#76263c] to-[#a54551] text-white rounded-xl shadow-md font-medium';
const unavailable = 'Not available yet';
function Card({ title, icon: Icon, color = '#76263c', children, className = '' }) {
  return <section className={`${cardClass} border-t-4 ${className}`} style={{ borderTopColor: color }}>
    <h3 className="text-xl mb-5 font-bold text-[#4a1825] flex items-center"><Icon className="mr-2" size={20} style={{ color }} />{title}</h3>
    {children}
  </section>;
}

export default function Home() {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [messageOpen, setMessageOpen] = useState(false);
  const mentor = String(userData.role).toLowerCase() === 'mentor';
  // Shared mentoring data has no reliable source yet. Never substitute legacy browser records.
  const expertise = Array.isArray(userData.expertise) ? userData.expertise.filter(value => typeof value === 'string') : [];
  return <AppLayout activeTab="home">
    <div className="bg-gradient-to-br from-[#fdf4ed] to-white p-8 rounded-3xl shadow-xl cytoplasm-bg">
      <h2 className="text-4xl mb-8 font-bold text-[#4a1825] font-serif">{mentor ? `Welcome, ${userData.name}` : 'Progress Dashboard'}</h2>
      {mentor ? <>
        <div className="vessel-container grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          <Card title="Clinical Statistics" icon={Activity} className="md:col-span-4">
            <div className="grid grid-cols-2 gap-4">
              {['Sessions', 'Mentees', 'Rating'].map(label => <div key={label} className="text-center p-4 bg-gradient-to-br from-[#fce8d420] to-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-[#76263c]" aria-label={`${label}: not available`}>—</div>
                <div className="text-sm text-gray-600 font-medium">{label}</div>
              </div>)}
            </div>
            <p className="text-sm text-gray-600 mt-4">Statistics and ratings are not available until shared mentoring activity is connected.</p>
            <h4 className="font-medium mt-4 text-[#4a1825]">Your selected interests</h4>
            {expertise.length ? <div className="flex flex-wrap gap-2 mt-2">{expertise.map(topic => <span key={topic} className="text-xs px-3 py-1 rounded-full border border-[#76263c30] text-[#76263c]">{topic}</span>)}</div> : <p className="text-sm text-gray-500 mt-2">No interests selected.</p>}
          </Card>
          <Card title="Clinical Actions" icon={GitBranch} color="#a54551" className="md:col-span-4">
            <div className="space-y-4">
              <button className={buttonClass} onClick={() => navigate('/app/mentor/listing')}>Manage Directory Listing</button>
              <button className={buttonClass} onClick={() => navigate('/app/profile?section=office-hours')}>Schedule Office Hours</button>
              <p className="text-sm text-gray-600">Save your own hours in Profile. Student booking is not connected yet.</p>
              <button className={buttonClass} onClick={() => setMessageOpen(true)}>Message Mentees</button>
              <button className={buttonClass} onClick={() => navigate('/app/reports/performance')}>View Clinical Reports</button>
            </div>
          </Card>
          <Card title="Medical Resources" icon={Microscope} color="#b65a1b" className="md:col-span-4">
            <p className="text-gray-600 mb-5">The resource library is not available yet. No sample downloads are shown as real resources.</p>
            <button className={buttonClass} onClick={() => navigate('/app/resources')}>View Library</button>
          </Card>
        </div>
        <Card title="Upcoming Sessions" icon={Calendar} className="mb-8">
          <p className="text-gray-600">Shared appointments are not available yet. Legacy browser bookings are not confirmed appointments.</p>
          <button disabled className={`${buttonClass} mt-4 opacity-60`}>Start Session — unavailable</button>
          <button disabled className={`${buttonClass} mt-3 opacity-60`}>Session Notes — unavailable</button>
        </Card>
        <Card title="Your Mentees" icon={Users} color="#a54551">
          <p className="text-gray-600 mb-4">Shared mentoring relationships are not available yet.</p>
          <button className={buttonClass} onClick={() => navigate('/app/mentees')}>View Mentees</button>
        </Card>
      </> : <>
        <div className="vessel-container grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          <section className={`${cardClass} md:col-span-4 border-t-4 border-[#76263c] flex flex-col items-center justify-center`}>
            <svg className="w-40 h-40" viewBox="0 0 100 100" role="img" aria-label="Progress assessment not available">
              <path d="M35 10 H65 V35 H90 V65 H65 V90 H35 V65 H10 V35 H35 Z" fill="#fef5ee" stroke="#76263c" strokeWidth="3" />
              <text x="50" y="54" textAnchor="middle" fontSize="22" fill="#76263c">—</text>
            </svg>
            <h3 className="text-xl mt-3 font-bold text-[#4a1825]">Medical Progress</h3>
            <p className="text-sm text-gray-500 text-center mt-2">Career scoring is {unavailable.toLowerCase()}. Your personal tracker is separate.</p>
          </section>
          <Card title="Clinical Achievements" icon={Award} color="#b65a1b" className="md:col-span-4"><p className="text-gray-600">Achievement tracking is not available yet.</p></Card>
          <Card title="Clinical Schedule" icon={Calendar} color="#a54551" className="md:col-span-4"><p className="text-gray-600">Shared appointments are not connected. Personal calendar entries stay in your browser's Progress tracker.</p></Card>
        </div>
        <Card title="Mentoring Sessions" icon={Activity} className="mb-8">
          <p className="text-gray-600 mb-4">Browse published mentor listings. Shared meeting requests are not available yet.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className={buttonClass} onClick={() => navigate('/app/mentor')}>Find a Mentor</button>
            <button className={buttonClass} onClick={() => setMessageOpen(true)}>Message Mentors</button>
          </div>
        </Card>
        <Card title="Medical Tasks" icon={Thermometer} color="#b65a1b">
          <p className="text-gray-600 mb-4">Personal tasks, notes, goals, and calendar entries can be kept in Progress. Automatic recommendations are not available yet.</p>
          <button className={buttonClass} onClick={() => navigate('/app/progress')}>Open Personal Progress</button>
        </Card>
      </>}
    </div>
    <MessageCenter isOpen={messageOpen} onClose={() => setMessageOpen(false)} />
  </AppLayout>;
}
